/**
 * SavedFilesDialog — global library of frozen form snapshots.
 *
 * Opens from the "My Files" pill in the header (admin-only-visible? — no,
 * every signed-in user has access; the backend filters what they can see).
 *
 *   • Lists every snapshot the user can read (own + shared; admin sees all).
 *   • Group-by-kind tabs (Inventory · Checklists · Permits · Insurer · Subcontractor).
 *   • Per-row actions: Open in editor, Re-export PDF/Excel, Share, Delete.
 *
 *  Each snapshot stores its full payload server-side, so "Open in editor"
 *  fires a CustomEvent (`open-snapshot`) with the snapshot blob — the
 *  individual form (e.g. InventoryTable) is the one that listens and
 *  hydrates itself from `event.detail.payload`.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FolderOpen, FileText, FileSpreadsheet, Trash2, Share2, Lock, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import SnapshotViewerDialog from './SnapshotViewerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  inventory: 'Inventory',
  checklist: 'Checklists',
  permit: 'Permits',
  insurer: 'Insurer',
  subcontractor: 'Subcontractor',
};
const ALL_KINDS = Object.keys(KIND_LABELS);

export default function SavedFilesDialog({ open, onClose, adminToken }) {
  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [activeKind, setActiveKind] = useState('all');
  const [err, setErr] = useState('');
  const [viewerSnap, setViewerSnap] = useState(null);

  const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : null;

  const load = async () => {
    if (!adminToken) return;
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.get(`${API}/snapshots`, auth);
      setSnapshots(data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load saved files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, adminToken]);

  const filtered = useMemo(
    () => snapshots.filter((s) => activeKind === 'all' || s.kind === activeKind),
    [snapshots, activeKind],
  );

  const restoreSnapshot = (snap) => {
    // Click on a saved file now ALWAYS opens the universal viewer first.
    // The viewer exposes a "Restore into form" button for kinds that
    // support legacy in-form hydration (currently inventory only), so the
    // existing `open-snapshot` event flow is preserved as an explicit
    // 2nd click.
    setViewerSnap(snap);
  };

  const deleteSnap = async (snap) => {
    if (!window.confirm(`Delete saved file "${snap.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/snapshots/${snap.id}`, auth);
      setSnapshots((s) => s.filter((x) => x.id !== snap.id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleShare = async (snap) => {
    try {
      const { data } = await axios.patch(`${API}/admin/snapshots/${snap.id}`, { shared: !snap.shared }, auth);
      setSnapshots((s) => s.map((x) => (x.id === snap.id ? data : x)));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to update share');
    }
  };

  const kindCounts = useMemo(() => {
    const c = { all: snapshots.length };
    ALL_KINDS.forEach((k) => { c[k] = snapshots.filter((s) => s.kind === k).length; });
    return c;
  }, [snapshots]);

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="saved-files-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <FolderOpen className="w-5 h-5 text-blue-600" />
            Saved Files
            <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">{snapshots.length}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Kind tabs */}
        <div className="flex flex-wrap gap-1.5 px-1 pt-1">
          {[{ key: 'all', label: 'All' }, ...ALL_KINDS.map((k) => ({ key: k, label: KIND_LABELS[k] }))].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveKind(t.key)}
              data-testid={`saved-files-tab-${t.key}`}
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                activeKind === t.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t.label}
              <span className="ml-1 opacity-70">({kindCounts[t.key] || 0})</span>
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10 text-gray-500" data-testid="saved-files-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading saved files…
          </div>
        )}

        {!loading && err && (
          <div className="text-sm text-red-600 py-4" data-testid="saved-files-error">{err}</div>
        )}

        {!loading && !err && filtered.length === 0 && (
          <div className="text-center py-10 text-gray-500 flex-1" data-testid="saved-files-empty">
            <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No saved files yet.</p>
            <p className="text-xs mt-1 text-gray-400">
              Open any form, fill it out, and click <strong>Save snapshot</strong> to keep a frozen copy here.
            </p>
          </div>
        )}

        {!loading && !err && filtered.length > 0 && (
          <ul className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1.5 mt-2" data-testid="saved-files-list">
            {filtered.map((s) => (
              <li
                key={s.id}
                data-testid={`saved-file-${s.id}`}
                className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                  {s.kind === 'inventory' ? <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-blue-600" />}
                </div>
                <button
                  type="button"
                  onClick={() => restoreSnapshot(s)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`saved-file-open-${s.id}`}
                  title="Open in editor"
                >
                  <p className="text-sm font-bold text-gray-900 truncate">{s.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {KIND_LABELS[s.kind] || s.kind} · {s.created_by_name} · {new Date(s.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => toggleShare(s)}
                  className={`p-1.5 rounded transition-colors ${s.shared ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-200'}`}
                  data-testid={`saved-file-share-${s.id}`}
                  title={s.shared ? 'Shared with team — click to make private' : 'Private — click to share with team'}
                >
                  {s.shared ? <Share2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => deleteSnap(s)}
                  className="p-1.5 rounded hover:bg-red-100 text-red-600"
                  data-testid={`saved-file-delete-${s.id}`}
                  title="Delete saved file"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
    <SnapshotViewerDialog
      snapshot={viewerSnap}
      open={!!viewerSnap}
      onClose={() => setViewerSnap(null)}
    />
    </>
  );
}
